import React, { useState, useEffect } from 'react';
import { MessageSquare, Clock, User, Tag, Trash2, Upload, Image as ImageIcon, X as XIcon, UserPlus, RefreshCw, FileDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import { toast } from '@/components/ui/use-toast';
import { updateSolicitud, addComentario, deleteSolicitud } from '@/services/solicitudesDesarrollo.service';
import { fetchUsers } from '@/services/users.service';
import { uploadDocumentFlow, getDownloadUrlOrOpen } from '@/services/documentsService';

const SolicitudDetailModal = ({ open, onClose, solicitud, onUpdate, user }) => {
  const [comentario, setComentario] = useState('');
  const [loading, setLoading] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(solicitud.estado);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [commentImages, setCommentImages] = useState([]);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [imageUrls, setImageUrls] = useState({});
  const [commentImageUrls, setCommentImageUrls] = useState({});

  useEffect(() => {
    if (open) {
      loadUsers();
      loadImageUrls();
      setSelectedStatus(solicitud.estado);
      setSelectedUser(solicitud.asignadoA?.id || null);
    }
  }, [open]);

  useEffect(() => {
    if (open && solicitud) {
      loadCommentImageUrls();
    }
  }, [open, solicitud.comentarios]);

  const loadUsers = async () => {
    try {
      setLoadingUsers(true);
      const response = await fetchUsers({ limit: 100, offset: 1 });
      const usersList = response?.data || response?.items || response || [];
      setUsers(Array.isArray(usersList) ? usersList : []);
    } catch (error) {
      console.error('Error loading users:', error);
      setUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  const loadImageUrls = async () => {
    if (!solicitud.adjuntos || solicitud.adjuntos.length === 0) return;
    
    const urls = {};
    for (const adjuntoId of solicitud.adjuntos) {
      try {
        const url = await getDownloadUrlOrOpen({ id: adjuntoId });
        urls[adjuntoId] = url;
      } catch (error) {
        console.error('Error loading image:', error);
      }
    }
    setImageUrls(urls);
  };

  const loadCommentImageUrls = async () => {
    if (!solicitud.comentarios) return;
    
    const urls = {};
    for (const comment of solicitud.comentarios) {
      if (comment.adjuntos && comment.adjuntos.length > 0) {
        for (const adjuntoId of comment.adjuntos) {
          try {
            const url = await getDownloadUrlOrOpen({ id: adjuntoId });
            urls[adjuntoId] = url;
          } catch (error) {
            console.error('Error loading comment image:', error);
          }
        }
      }
    }
    setCommentImageUrls(urls);
  };

  const handleCommentImageSelect = (e) => {
    const files = Array.from(e.target.files || []);
    const imageFiles = files.filter(file => file.type.startsWith('image/'));
    
    if (imageFiles.length !== files.length) {
      toast({
        title: 'Advertencia',
        description: 'Solo se permiten archivos de imagen',
        variant: 'destructive',
      });
    }
    
    setCommentImages(prev => [...prev, ...imageFiles]);
  };

  const handleRemoveCommentImage = (index) => {
    setCommentImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleAddComentario = async () => {
    if (!comentario.trim()) return;

    try {
      setLoading(true);
      
      // Upload comment images first
      let uploadedImageIds = [];
      if (commentImages.length > 0) {
        setUploadingImages(true);
        try {
          const uploadPromises = commentImages.map(file => 
            uploadDocumentFlow({
              ownerType: 'solicitud',
              ownerId: solicitud._id,
              docType: 'imagen',
              file,
            })
          );
          const uploadResults = await Promise.all(uploadPromises);
          uploadedImageIds = uploadResults.map(result => result.id || result._id);
        } catch (uploadError) {
          console.error('Error uploading images:', uploadError);
          toast({
            title: 'Advertencia',
            description: 'Algunas imágenes no se pudieron subir',
            variant: 'destructive',
          });
        } finally {
          setUploadingImages(false);
        }
      }
      
      await addComentario(solicitud._id, {
        usuario: {
          id: user.id,
          nombre: user.name,
        },
        contenido: comentario,
        adjuntos: uploadedImageIds,
      });
      setComentario('');
      setCommentImages([]);
      toast({
        title: 'Comentario agregado',
        description: 'El comentario se ha agregado exitosamente',
      });
      onUpdate();
    } catch (error) {
      console.error('Error adding comentario:', error);
      toast({
        title: 'Error',
        description: 'No se pudo agregar el comentario',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAssignUser = async () => {
    try {
      setLoading(true);
      
      const selectedUserData = users.find(u => u._id === selectedUser);
      const updateData = {
        asignadoA: selectedUser ? {
          id: selectedUserData._id,
          nombre: selectedUserData.nombre,
          email: selectedUserData.correo,
        } : { id: null, nombre: null, email: null }
      };
      
      await updateSolicitud(
        solicitud._id,
        updateData,
        { id: user.id, nombre: user.name }
      );
      
      toast({
        title: 'Usuario asignado',
        description: 'El usuario ha sido asignado exitosamente',
      });
      
      setShowAssignModal(false);
      onUpdate();
    } catch (error) {
      console.error('Error assigning user:', error);
      toast({
        title: 'Error',
        description: 'No se pudo asignar el usuario',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChangeStatus = async () => {
    try {
      setLoading(true);
      
      await updateSolicitud(
        solicitud._id,
        { estado: selectedStatus },
        { id: user.id, nombre: user.name }
      );
      
      toast({
        title: 'Estado actualizado',
        description: 'El estado ha sido actualizado exitosamente',
      });
      
      setShowStatusModal(false);
      onUpdate();
    } catch (error) {
      console.error('Error updating status:', error);
      toast({
        title: 'Error',
        description: 'No se pudo actualizar el estado',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm('¿Estás seguro de que deseas eliminar esta solicitud?')) {
      return;
    }

    try {
      setLoading(true);
      await deleteSolicitud(solicitud._id);
      toast({
        title: 'Solicitud eliminada',
        description: 'La solicitud se ha eliminado exitosamente',
      });
      onClose();
      onUpdate();
    } catch (error) {
      console.error('Error deleting solicitud:', error);
      toast({
        title: 'Error',
        description: 'No se pudo eliminar la solicitud',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const getEstadoColor = (estado) => {
    switch (estado) {
      case 'pendiente':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'en_progreso':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'completado':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'cerrado':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
      case 'cancelado':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <DialogTitle className="text-2xl">{solicitud.titulo}</DialogTitle>
              <DialogDescription className="mt-2">
                Creado por {solicitud.creadoPor?.nombre} el{' '}
                {new Date(solicitud.fechaCreacion).toLocaleDateString()}
              </DialogDescription>
            </div>
            {/* <Button
              variant="ghost"
              size="icon"
              onClick={handleDelete}
              className="text-red-600 hover:text-red-700"
            >
              <Trash2 className="h-4 w-4" />
            </Button> */}
          </div>
        </DialogHeader>

        <div className="space-y-6">
          <div className="flex flex-wrap gap-2 items-center">
            <Badge variant="outline">{solicitud.tipo}</Badge>
            <Badge>{solicitud.prioridad}</Badge>
            <Badge className={getEstadoColor(solicitud.estado)}>
              {solicitud.estado.replace('_', ' ')}
            </Badge>
            {solicitud.modulo && <Badge variant="secondary">{solicitud.modulo}</Badge>}
          </div>

          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowAssignModal(true)}
              className="border-blue-500 text-blue-600 hover:bg-blue-50 hover:text-blue-700"
            >
              <UserPlus className="h-4 w-4 mr-2" />
              {solicitud.asignadoA ? 'Cambiar asignación' : 'Asignar usuario'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowStatusModal(true)}
              className="border-green-500 text-green-600 hover:bg-green-50 hover:text-green-700"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Cambiar estado
            </Button>
          </div>

          <div>
            <h3 className="font-semibold mb-2">Descripción</h3>
            <p className="text-sm text-muted-foreground whitespace-pre-wrap">
              {solicitud.descripcion}
            </p>
          </div>

          {solicitud.asignadoA && (
            <div className="flex items-center gap-2">
              <User className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm">
                Asignado a: <strong>{solicitud.asignadoA.nombre}</strong>
              </span>
            </div>
          )}

          {solicitud.adjuntos && solicitud.adjuntos.length > 0 && (
            <div>
              <h3 className="font-semibold mb-2 flex items-center gap-2">
                <ImageIcon className="h-4 w-4" />
                Imágenes adjuntas ({solicitud.adjuntos.length})
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {solicitud.adjuntos.map((adjuntoId) => (
                  <div key={adjuntoId} className="relative group">
                    {imageUrls[adjuntoId] ? (
                      <img
                        src={imageUrls[adjuntoId]}
                        alt="Adjunto"
                        className="w-full h-32 object-cover rounded-lg cursor-pointer hover:opacity-80 transition-opacity"
                        onClick={() => window.open(imageUrls[adjuntoId], '_blank')}
                      />
                    ) : (
                      <div className="w-full h-32 bg-muted rounded-lg flex items-center justify-center">
                        <ImageIcon className="h-8 w-8 text-muted-foreground" />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          <Separator />

          <div>
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <MessageSquare className="h-4 w-4" />
              Comentarios ({solicitud.comentarios?.length || 0})
            </h3>

            <div className="space-y-4 mb-4">
              {solicitud.comentarios?.map((com) => (
                <div key={com._id} className="bg-muted p-4 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold text-sm">{com.usuario.nombre}</span>
                    <span className="text-xs text-muted-foreground">
                      {new Date(com.fecha).toLocaleString()}
                    </span>
                  </div>
                  <p className="text-sm whitespace-pre-wrap">{com.contenido}</p>
                  {com.adjuntos && com.adjuntos.length > 0 && (
                    <div className="mt-3 grid grid-cols-2 gap-2">
                      {com.adjuntos.map((adjuntoId) => (
                        <div key={adjuntoId} className="relative group">
                          {commentImageUrls[adjuntoId] ? (
                            <img
                              src={commentImageUrls[adjuntoId]}
                              alt="Adjunto"
                              className="w-full h-24 object-cover rounded cursor-pointer hover:opacity-80 transition-opacity"
                              onClick={() => window.open(commentImageUrls[adjuntoId], '_blank')}
                            />
                          ) : (
                            <div className="w-full h-24 bg-background rounded flex items-center justify-center">
                              <ImageIcon className="h-6 w-6 text-muted-foreground" />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="space-y-2">
              <Textarea
                value={comentario}
                onChange={(e) => setComentario(e.target.value)}
                placeholder="Escribe un comentario..."
                rows={3}
              />
              
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleCommentImageSelect}
                    className="hidden"
                    id="comment-image-upload"
                    disabled={loading || uploadingImages}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => document.getElementById('comment-image-upload').click()}
                    disabled={loading || uploadingImages}
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    Adjuntar imágenes
                  </Button>
                  {commentImages.length > 0 && (
                    <span className="text-sm text-muted-foreground">
                      {commentImages.length} imagen(es) seleccionada(s)
                    </span>
                  )}
                </div>
                
                {commentImages.length > 0 && (
                  <div className="grid grid-cols-2 gap-2">
                    {commentImages.map((file, index) => (
                      <div key={index} className="relative border rounded-lg p-2 flex items-center gap-2">
                        <ImageIcon className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm truncate flex-1">{file.name}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => handleRemoveCommentImage(index)}
                          disabled={loading || uploadingImages}
                          className="h-6 w-6"
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              <Button onClick={handleAddComentario} disabled={loading || uploadingImages || !comentario.trim()}>
                {uploadingImages ? 'Subiendo imágenes...' : 'Agregar Comentario'}
              </Button>
            </div>
          </div>
{/* 
          {solicitud.historial && solicitud.historial.length > 0 && (
            <>
              <Separator />
              <div>
                <h3 className="font-semibold mb-4 flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  Historial de Cambios
                </h3>
                <div className="space-y-2">
                  {solicitud.historial.map((cambio) => (
                    <div key={cambio._id} className="text-sm text-muted-foreground">
                      <span className="font-medium">{cambio.usuario.nombre}</span> cambió{' '}
                      <strong>{cambio.campo}</strong> de "{cambio.valorAnterior}" a "
                      {cambio.valorNuevo}" el {new Date(cambio.fecha).toLocaleString()}
                    </div>
                  ))}
                </div>
              </div>
            </>
          )} */}
        </div>
      </DialogContent>

      {/* Modal para cambiar asignación */}
      <Dialog open={showAssignModal} onOpenChange={setShowAssignModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Asignar usuario</DialogTitle>
            <DialogDescription>
              Selecciona un usuario para asignar esta solicitud
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Select
              value={selectedUser || "unassigned"}
              onValueChange={(value) => setSelectedUser(value === "unassigned" ? null : value)}
            >
              <SelectTrigger>
                <SelectValue placeholder={loadingUsers ? "Cargando usuarios..." : "Seleccionar usuario"} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="unassigned">Sin asignar</SelectItem>
                {users.map((u) => (
                  <SelectItem key={u._id} value={u._id}>
                    {u.nombre} ({u.correo})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowAssignModal(false)}>
                Cancelar
              </Button>
              <Button onClick={handleAssignUser} disabled={loading}>
                Asignar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal para cambiar estado */}
      <Dialog open={showStatusModal} onOpenChange={setShowStatusModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Cambiar estado</DialogTitle>
            <DialogDescription>
              Selecciona el nuevo estado para esta solicitud
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Select
              value={selectedStatus}
              onValueChange={setSelectedStatus}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pendiente">Pendiente</SelectItem>
                <SelectItem value="en_progreso">En Progreso</SelectItem>
                <SelectItem value="completado">Completado</SelectItem>
                <SelectItem value="cerrado">Cerrado</SelectItem>
                <SelectItem value="cancelado">Cancelado</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowStatusModal(false)}>
                Cancelar
              </Button>
              <Button onClick={handleChangeStatus} disabled={loading}>
                Actualizar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};

export default SolicitudDetailModal;
